<template>
  <div class="profile-menu-content" :class="{ 'profile-menu-content--mobile': isMobile }" :style="hoverStyle">
    <!-- User Email -->
    <div class="user-email-section">
      <VaIcon name="mso-mail" size="14px" color="secondary" class="mr-2" />
      <span class="text-sm text-[var(--va-secondary)] truncate">{{ userEmail }}</span>
    </div>

    <!-- Menu Groups -->
    <div class="menu-groups">
      <div v-for="group in options" :key="group.name" class="menu-group">
        <header v-if="group.name && !isMobile" class="menu-group-header">
          {{ t(`user.${group.name}`) }}
        </header>

        <VaListItem
          v-for="item in group.list"
          :key="item.name"
          class="menu-item"
          v-bind="resolveLinkAttribute(item)"
          @click="handleItemClick(item.name)"
        >
          <VaIcon :name="item.icon" size="16px" class="menu-item-icon" color="secondary" />
          <span class="menu-item-text">{{ t(`user.${item.name}`) }}</span>
        </VaListItem>

        <!-- Dark Mode Toggle (after preferences group) -->
        <div v-if="group.name === 'preferences'" class="menu-item dark-mode-toggle">
          <VaIcon :name="darkModeIcon" size="16px" class="menu-item-icon" color="secondary" />
          <span class="menu-item-text flex-grow cursor-pointer" @click="handleDarkModeClick">{{
            t('user.darkMode')
          }}</span>
          <VaSwitch v-model="darkModeModel" :loading="isDarkModeLoading" :disabled="isDarkModeLoading" size="small" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { useRouter } from 'vue-router'
import { useMsal } from '../../../../azure/useMsal'
import { useCurrentUserProfile } from '../../../../composables/useCurrentUserProfile'

// Types
type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

const props = withDefaults(
  defineProps<{
    options?: ProfileOptions[]
    isMobile?: boolean
  }>(),
  {
    options: () => [],
    isMobile: false,
  },
)

const { t } = useI18n()
const router = useRouter()
const { accounts, instance } = useMsal()
const { isDarkMode, isDarkModeLoading, toggleDarkMode } = useCurrentUserProfile()
const { getColor, colorToRgba } = useColors()

const hoverColor = computed(() => colorToRgba(getColor('focus'), 0.1))
const hoverStyle = computed(() => ({ '--hover-color': hoverColor.value }))

const userEmail = computed(() => {
  if (accounts.value.length > 0) {
    return accounts.value[0].username
  }
  return 'N/A'
})

const darkModeModel = computed({
  get: () => {
    return isDarkMode.value
  },
  set: (value: boolean) => {
    // Prevent toggling if already in progress or value hasn't changed
    if (value === isDarkMode.value || isDarkModeLoading.value) return
    void toggleDarkMode()
  },
})

const darkModeIcon = computed(() => (isDarkMode.value ? 'mso-dark_mode' : 'mso-light_mode'))

const resolveLinkAttribute = (item: ProfileListItem) => {
  // VaListItem doesn't support router-link 'to' prop, so we return empty
  // Navigation will be handled in handleItemClick
  return item.href ? { href: item.href, target: '_blank' } : {}
}

const handleLogout = async () => {
  console.log('[handleLogout] Logging out...')
  // Set flag to prevent auto-redirect to B2C after logout
  try {
    sessionStorage.setItem('justLoggedOut', 'true')
  } catch {
    // ignore storage errors
  }
  // Use logoutRedirect instead of logoutPopup to avoid "Leave site?" browser prompts
  await instance.logoutRedirect({
    postLogoutRedirectUri: '/auth/login',
  })
}

const handleDarkModeClick = (event: Event) => {
  event?.stopPropagation()
  event?.preventDefault()
  // Prevent toggling if already in progress
  if (isDarkModeLoading.value) return
  void toggleDarkMode()
}

const handleProfileClick = () => {
  router.push({ name: 'profile' })
}

const actionHandlers: Record<string, () => void> = {
  logout: handleLogout,
  profile: handleProfileClick,
}

const handleItemClick = (itemName: string) => {
  if (actionHandlers[itemName]) {
    actionHandlers[itemName]()
  }
}
</script>

<style lang="scss">
.profile-menu-content {
  .user-email-section {
    display: flex;
    align-items: center;
    padding: 0.5rem 1rem;
  }

  .menu-groups {
    padding: 0;
  }

  .menu-group-header {
    font-size: 0.625rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--va-secondary);
    opacity: 0.7;
    padding: 0.375rem 1rem 0.125rem;
  }

  .menu-item {
    display: flex;
    align-items: center;
    padding: 0.5rem 1rem;
    cursor: pointer;
    transition: background-color 0.15s ease;
    border-radius: 0;

    &:hover {
      background: var(--hover-color);
    }
  }

  .menu-item-icon {
    margin-right: 0.625rem;
    flex-shrink: 0;
  }

  .menu-item-text {
    font-size: 0.8125rem;
    color: var(--va-text-primary);
  }

  .dark-mode-toggle {
    cursor: default;

    .menu-item-text {
      cursor: pointer;
    }
  }

  // Mobile-specific styles
  &--mobile {
    .user-email-section {
      padding: 0.5rem 1rem;
    }

    .menu-item {
      padding: 0.5rem 1rem;
    }

    .menu-item-text {
      font-size: 0.875rem;
    }
  }
}
</style>
